import { Alert, Typography } from 'antd'
import {
  unwrapFieldValue,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { Dispatch, SetStateAction } from 'react'
import styled from 'styled-components'

import { InsulationPreparationCard } from './InsulationPreparationCard'
import {
  getPlannedInsulationRemoval,
  isInsulationPreparationComplete,
  readInsulationPreparationRows,
  updateInsulationPreparation,
} from './insulationPreparationModel'
import {
  InsulationPreparation,
  insulationPreparationSchema,
  InsulationPreparationSection,
} from './insulationPreparationTypes'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

export const InsulationPreparationSectionView = ({
  config,
  fieldValues,
  setFieldValues,
}: {
  config: InsulationPreparationSection
  fieldValues: FieldValues
  setFieldValues: Dispatch<SetStateAction<FieldValues>> | undefined
}) => {
  const { globalContext } = useWorkflow()
  const preparation = readInsulationPreparationRows(
    config,
    fieldValues,
    globalContext
  )
  if (!preparation.success)
    return (
      <Alert
        type='warning'
        showIcon
        title='Insulation preparation needs attention'
        description='The source assessment, preparation entries or strategy settings could not be read. This section cannot be completed until they are corrected.'
      />
    )
  const rows = preparation.data
  const complete = isInsulationPreparationComplete(rows)
  const total = complete
    ? rows.reduce(
        (sum, row) => sum + (getPlannedInsulationRemoval(row) ?? 0),
        0
      )
    : undefined
  const onChange = (
    sourceZoneId: string,
    update: Partial<Omit<InsulationPreparation, 'sourceZoneId'>>
  ) => {
    setFieldValues?.((previous) => {
      const current = insulationPreparationSchema
        .array()
        .safeParse(unwrapFieldValue(previous[config.field]) ?? [])
      if (!current.success) return previous
      return {
        ...previous,
        [config.field]: wrapFieldValue(
          updateInsulationPreparation(current.data, sourceZoneId, update)
        ),
      }
    })
  }

  return (
    <Container>
      <Typography.Paragraph>
        Identify the insulated zones. The estimate uses each zone’s source NII
        inspection area; adjust the planned quantity for site preparation.
      </Typography.Paragraph>
      {rows.map((row) => (
        <InsulationPreparationCard
          key={row.sourceZoneId}
          row={row}
          readonly={!setFieldValues}
          onChange={onChange}
        />
      ))}
      {total !== undefined && (
        <Typography.Text strong>
          Total planned removal:{' '}
          {total.toLocaleString(undefined, { maximumFractionDigits: 2 })} m²
        </Typography.Text>
      )}
    </Container>
  )
}
